import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MailCheck, Loader2, ShieldCheck, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { backendApi } from '@/lib/backendApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const VerifyEmailPage = () => {
  const { api, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const emailFromUrl = useMemo(() => searchParams.get('email') || '', [searchParams]);

  const [email, setEmail] = useState(emailFromUrl);
  const [code, setCode] = useState('');
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('verify');

  const clearNotices = () => {
    setMessage('');
    setError('');
  };

  const handleVerificationSuccess = (data) => {
    if (data?.access_token) {
      localStorage.setItem('accessToken', data.access_token);
    }
    if (data?.refresh_token) {
      localStorage.setItem('refreshToken', data.refresh_token);
    }
    setMessage('Email verified. Redirecting to business setup...');
    setTimeout(() => {
      window.location.assign('/setup');
    }, 800);
  };

  const verifyWithToken = async (token) => {
    setLoadingVerify(true);
    clearNotices();
    try {
      const response = await backendApi.auth.verifyEmail(api, { token });
      handleVerificationSuccess(response?.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Email verification failed.');
    } finally {
      setLoadingVerify(false);
    }
  };

  useEffect(() => {
    if (tokenFromUrl) {
      verifyWithToken(tokenFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromUrl]);

  const onManualVerify = async (e) => {
    e.preventDefault();
    setLoadingVerify(true);
    clearNotices();
    try {
      const response = await backendApi.auth.verifyEmail(api, { email, code });
      handleVerificationSuccess(response?.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Email verification failed.');
    } finally {
      setLoadingVerify(false);
    }
  };

  const onResendCode = async (e) => {
    e.preventDefault();
    setLoadingResend(true);
    clearNotices();
    try {
      const response = await backendApi.auth.requestEmailVerification(api, { email });
      setMessage(response?.data?.message || 'If eligible, a verification email has been sent.');
      setActiveTab('verify');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send verification email.');
    } finally {
      setLoadingResend(false);
    }
  };

  const handleGoToAuthPage = async (e, path) => {
    e.preventDefault();
    await logout();
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-6">
        <div className="hidden lg:flex relative overflow-hidden rounded-2xl border border-border/50 bg-card/60 shadow-xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.18),transparent_45%),radial-gradient(circle_at_80%_80%,rgba(56,189,248,0.16),transparent_45%)]" />
          <div className="relative z-10 p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3">
                <img
                  src="/survipos%20logo.png"
                  alt="SurviPOS logo"
                  className="h-12 w-12 object-cover"
                />
                <h1 className="font-heading text-4xl font-bold text-primary">SurviPOS</h1>
              </div>
              <p className="text-muted-foreground">Secure owner verification</p>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-primary mt-0.5" />
                <p className="text-sm text-foreground">Only verified owner accounts can access setup and billing controls.</p>
              </div>
              <div className="flex items-start gap-3">
                <MailCheck className="h-5 w-5 text-secondary mt-0.5" />
                <p className="text-sm text-foreground">Use either the email link or 6-digit verification code.</p>
              </div>
            </div>
          </div>
        </div>

        <Card className="border-border/50 bg-card/75 shadow-xl backdrop-blur">
          <CardHeader>
            <CardTitle className="font-heading text-2xl flex items-center gap-2">
              <MailCheck className="h-6 w-6 text-primary" />
              Verify Owner Email
            </CardTitle>
            <CardDescription>
              Enter your email and code, or request a new verification email.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {message ? (
              <Alert className="border-primary/40 bg-primary/10 text-primary">
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            ) : null}
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="verify">Verify Code</TabsTrigger>
                <TabsTrigger value="resend">Resend Email</TabsTrigger>
              </TabsList>

              <TabsContent value="verify">
                <form onSubmit={onManualVerify} className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label htmlFor="verify-email">Email</Label>
                    <Input
                      id="verify-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="verify-code">6-digit code</Label>
                    <Input
                      id="verify-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="123456"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                      required
                      className="h-11 tracking-[0.25em] font-mono"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11 font-semibold" disabled={loadingVerify}>
                    {loadingVerify ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify Email'
                    )}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="resend">
                <form onSubmit={onResendCode} className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label htmlFor="resend-email">Owner email</Label>
                    <Input
                      id="resend-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <Button type="submit" variant="secondary" className="w-full h-11 font-semibold" disabled={loadingResend}>
                    {loadingResend ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <RefreshCcw className="mr-2 h-4 w-4" />
                        Send New Verification Email
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="pt-2 text-sm text-muted-foreground flex flex-wrap gap-3">
              <Link to="/login" onClick={(e) => handleGoToAuthPage(e, '/login')} className="text-primary hover:underline">Back to login</Link>
              <Link to="/register" onClick={(e) => handleGoToAuthPage(e, '/register')} className="text-secondary hover:underline">Create new account</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
